import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Trophy, Sparkles, X, Crown } from "lucide-react";

export const NATURAL_STATS = [
  ["speed", "Speed"], ["acceleration", "Acceleration"], ["agility", "Agility"],
  ["strength", "Strength"], ["stamina", "Stamina"], ["balance", "Balance"],
  ["athleticism", "Athleticism"], ["responsiveness", "Responsiveness"], ["bravery", "Bravery"],
  ["cow_sense", "Cow Sense"], ["movement", "Movement"], ["scope", "Scope"],
];

const REQUIRED_SKILLS = ["Halter broke", "Leads well", "Gives all four feet", "Ties patiently",
  "Loads on trailer", "Desensitized to stimuli", "Accepts saddle", "Started under saddle", "Rides w/t/c"];

const DISCIPLINES = ["Barrel Racing", "Cutting", "Dressage", "Reining", "Jumpers", "Eventing", "Driving"];

const WEIGHTS = {
  "Barrel Racing": { speed: 2, acceleration: 2, agility: 2, balance: 2, athleticism: 1, stamina: 1, responsiveness: 1 },
  "Cutting": { cow_sense: 2, agility: 2, responsiveness: 2, balance: 2, acceleration: 1, athleticism: 1, strength: 1 },
  "Dressage": { movement: 2, balance: 2, responsiveness: 2, athleticism: 2, strength: 1, stamina: 1, agility: 1 },
  "Reining": { agility: 2, balance: 2, responsiveness: 2, strength: 2, acceleration: 1, athleticism: 1, stamina: 1 },
  "Jumpers": { scope: 2, strength: 2, agility: 2, bravery: 2, speed: 1, balance: 1, athleticism: 1 },
  "Eventing": { stamina: 2, bravery: 2, athleticism: 2, scope: 2, speed: 1, balance: 1, movement: 1 },
  "Driving": { responsiveness: 2, stamina: 2, strength: 2, movement: 2, balance: 1, bravery: 1, athleticism: 1 },
};

const label = (s) => s >= 88 ? "Exceptional" : s >= 78 ? "Excellent" : s >= 68 ? "Very Good"
  : s >= 58 ? "Good" : s >= 45 ? "Average" : s >= 33 ? "Below Average" : "Poor";
const labelColor = (s) => s >= 78 ? "text-emerald-600" : s >= 58 ? "text-green-600"
  : s >= 45 ? "text-amber-600" : "text-muted-foreground";
const ordinal = (n) => { if (!n) return "—"; const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

export const aptitudes = (stats) => DISCIPLINES.map((d) => {
  const w = WEIGHTS[d];
  const den = Object.values(w).reduce((a, b) => a + b, 0);
  const score = Math.round(Object.entries(w).reduce((a, [k, wt]) => a + (stats?.[k] || 0) * wt, 0) / den);
  return { discipline: d, score, label: label(score) };
}).sort((a, b) => b.score - a.score);

const isEligible = (a) => a.decision === "keep" && REQUIRED_SKILLS.every((s) => (a.learned_skills || []).includes(s));

const barColor = (v) => v >= 80 ? "#059669" : v >= 60 ? "#16a34a" : v >= 45 ? "#d97706" : "#9ca3af";

export const NaturalStatsPanel = ({ a }) => {
  const stats = a.natural_stats || {};
  return (
    <div className="mx-4 mt-3 space-y-3" data-testid="natural-stats-panel">
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-secondary">Natural Athletic Stats</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">Permanent inherent ability — separate from training. These help you decide the best career.</p>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {NATURAL_STATS.map(([k, lbl]) => (
            <div key={k} data-testid={`nat-stat-${k}`}>
              <div className="flex justify-between text-xs font-semibold text-primary"><span>{lbl}</span><span>{stats[k] ?? 0}/100</span></div>
              <div className="mt-0.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full" style={{ width: `${stats[k] ?? 0}%`, background: barColor(stats[k] ?? 0) }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-secondary">Discipline Aptitude</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">Estimated natural suitability. You can still choose any discipline you like.</p>
        <div className="mt-2 space-y-1.5">
          {aptitudes(stats).map((ap, i) => (
            <div key={ap.discipline} data-testid={`aptitude-${ap.discipline.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-1.5">
              <span className="text-sm font-semibold text-primary">{i === 0 && <Sparkles size={12} className="mr-1 inline text-amber-500" />}{ap.discipline}</span>
              <span className={`text-sm font-bold ${labelColor(ap.score)}`}>{ap.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const DisciplinePanel = ({ a }) => {
  const [discipline, setDiscipline] = useState(a.discipline || null);
  const [pick, setPick] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const eligible = isEligible(a);
  const apts = aptitudes(a.natural_stats || {});
  const aptOf = (d) => apts.find((x) => x.discipline === d);

  const confirm = async () => {
    setBusy(true);
    try {
      await gameApi.post(`/game/alumni/${a.id}/discipline`, { discipline: pick });
      setDiscipline(pick); setConfirming(false);
      toast.success(`${a.name} is now a ${pick} horse! 🏆`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set discipline"); }
    finally { setBusy(false); }
  };

  if (discipline) {
    return (
      <div className="mx-4 mt-3" data-testid="discipline-panel">
        <div className="rounded-2xl border-2 border-emerald-300 bg-card p-5 text-center">
          <Trophy size={28} className="mx-auto text-amber-500" />
          <p className="mt-2 text-xs font-bold uppercase tracking-wide text-emerald-600">Permanent Career</p>
          <p className="font-display text-2xl font-bold text-primary" data-testid="career-discipline">{discipline}</p>
          <p className="mt-1 text-xs text-muted-foreground">Level: {a.career?.level || "Novice"} · This choice is permanent.</p>
        </div>
      </div>
    );
  }
  if (!eligible) {
    return (
      <div className="mx-4 mt-3 space-y-3" data-testid="discipline-panel">
        <div className="rounded-2xl border border-dashed border-border bg-card/60 p-5 text-center">
          <p className="font-display text-lg font-bold text-primary">Choose a discipline after the makeover</p>
          <p className="mt-1 text-sm text-muted-foreground">You can pick a permanent competition discipline once this horse finishes all 120 days and every foundation training bar. Until then, here's how its natural talent stacks up:</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-secondary">Discipline Aptitude</p>
          <div className="mt-2 space-y-1.5">
            {apts.map((ap, i) => (
              <div key={ap.discipline} data-testid={`apt-locked-${ap.discipline.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-1.5">
                <span className="text-sm font-semibold text-primary">{i === 0 && <Sparkles size={12} className="mr-1 inline text-amber-500" />}{ap.discipline}</span>
                <span className={`text-sm font-bold ${labelColor(ap.score)}`}>{ap.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="mx-4 mt-3" data-testid="discipline-panel">
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50/50 p-3 text-center">
        <p className="text-sm font-bold text-emerald-700">✓ Eligible to begin an advanced career</p>
      </div>
      <p className="mt-3 text-xs font-bold uppercase tracking-wider text-secondary">Choose a permanent discipline</p>
      <div className="mt-2 grid grid-cols-1 gap-2">
        {DISCIPLINES.map((d) => {
          const ap = aptOf(d);
          return (
            <button key={d} data-testid={`choose-discipline-${d.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => { setPick(d); setConfirming(true); }}
              className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-left active:scale-[0.99]">
              <span className="font-bold text-primary">{d}</span>
              <span className={`text-sm font-bold ${labelColor(ap.score)}`}>{ap.label}</span>
            </button>
          );
        })}
      </div>

      {confirming && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/60 p-4" data-testid="discipline-confirm-modal">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 text-center">
            <AlertTriangle size={28} className="mx-auto text-amber-500" />
            <p className="mt-2 font-display text-xl font-bold text-primary">Choose {pick}?</p>
            <p className="mt-2 text-sm text-muted-foreground">Choose this horse's career carefully. Once you select a discipline, this horse will permanently specialize in that discipline. This choice cannot be changed.</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="discipline-cancel" disabled={busy} onClick={() => setConfirming(false)} className="h-11 flex-1 rounded-xl border-2 border-border font-bold text-primary">Cancel</button>
              <button data-testid="discipline-confirm" disabled={busy} onClick={confirm} className="h-11 flex-1 rounded-xl bg-emerald-600 font-bold text-white">{busy ? "…" : "Confirm Discipline"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdvancedTrainingPanel = ({ a, save, onSaveUpdate, onEndDay, onGoCare, onCompete, busy }) => {
  const disc = a.discipline;
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState([]);
  const [localBusy, setLocalBusy] = useState(false);
  useEffect(() => { gameApi.get(`/game/discipline-activities?discipline=${encodeURIComponent(disc)}`).then((r) => setMeta(r.data)).catch(() => setMeta(null)); }, [disc]);
  const working = busy || localBusy;
  const energy = Math.round(save?.stats?.energy ?? 100);
  const care = save?.care || {};
  const careDone = (care.hay && care.water) || save?.groom_active;
  const trained = care.trained;
  const bars = save?.advanced_bars || a.advanced_bars || {};
  const level = save?.training_level || a.training_level || "Novice";
  const acts = meta?.activities || [];
  const barKeys = meta?.bars || Object.keys(bars);
  const isElite = level === "Elite";
  const allFull = barKeys.length > 0 && barKeys.every((b) => (bars[b] ?? 0) >= 100);
  const eOf = (name) => (acts.find((x) => x.name === name)?.energy) || 20;
  const energyUsed = selected.reduce((s, n) => s + eOf(n), 0);
  const isLocked = (act) => !selected.includes(act.name) && act.energy > energy - energyUsed;
  const ptsLabel = (act) => Object.entries(act.points || {}).map(([b, p]) => `+${p} ${b}`).join(", ");
  const toggle = (act) => {
    if (selected.includes(act.name)) { setSelected(selected.filter((x) => x !== act.name)); return; }
    if (isLocked(act) || selected.length >= 2) return;
    setSelected([...selected, act.name]);
  };
  const doTrain = async (rest) => {
    setLocalBusy(true);
    try {
      const { data } = await gameApi.post(`/game/alumni/${a.id}/train-advanced`, rest ? { rest: true } : { activities: selected });
      setSelected([]); onSaveUpdate && onSaveUpdate(data.save);
      if (data.leveled_up) toast.success(`🎉 ${a.name} reached ${data.training_level} training level!`);
    } catch (e) { toast.error(e.response?.data?.detail || "Training failed"); }
    finally { setLocalBusy(false); }
  };
  return (
    <div className="mx-4 mt-3 space-y-3" data-testid="advanced-training-panel">
      <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2 text-sm font-bold text-primary">
        <span data-testid="adv-energy">⚡ Energy {energy}/100</span>
        <span className="text-secondary">{disc}</span>
      </div>
      {!careDone && (
        <button data-testid="adv-care-reminder" onClick={onGoCare} className="flex w-full items-center gap-2 rounded-xl border border-amber-400/60 bg-amber-50 px-3 py-2.5 text-left text-xs font-semibold text-amber-700 active:scale-[0.99]">
          <AlertTriangle size={15} /> Daily care comes first — feed hay &amp; fill water in the Care tab before training.
        </button>
      )}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-primary" data-testid="adv-training-level">Training Level: <span className="text-secondary">{level}</span></p>
          {isElite && allFull && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700" data-testid="adv-maxed">Max Potential 🏆</span>}
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">{isElite ? "Fill every bar to reach this horse's highest potential." : "Fill every skill bar to advance to the next training level."}</p>
        <div className="mt-3 space-y-2">
          {barKeys.map((b) => (
            <div key={b} data-testid={`adv-bar-${b.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex justify-between text-xs font-semibold text-primary"><span>{b}</span><span>{bars[b] ?? 0}/100</span></div>
              <div className="mt-0.5 h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${bars[b] ?? 0}%` }} /></div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-secondary">Training Activities</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">Pick up to 2 · each costs energy and raises specific bars.</p>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {acts.map((act) => {
            const sel = selected.includes(act.name);
            const lock = !sel && isLocked(act);
            return (
              <button key={act.name} data-testid={`adv-activity-${act.name.replace(/\s+/g, "-").toLowerCase()}`} disabled={!careDone || trained || lock} onClick={() => toggle(act)}
                className={`rounded-xl border px-3 py-2 text-left text-xs font-bold active:scale-95 ${sel ? "border-primary bg-primary/10 text-primary" : lock ? "border-border bg-muted/40 text-muted-foreground opacity-60" : "border-border bg-muted/40 text-primary"}`}>
                <span className="block leading-tight">{act.name}</span>
                <span className="mt-0.5 block text-[10px] font-semibold text-secondary">⚡{act.energy} · {ptsLabel(act)}</span>
              </button>
            );
          })}
        </div>
        {selected.length > 0 && <p className="mt-2 text-xs font-semibold text-secondary" data-testid="adv-selected">Selected: {selected.join(", ")} · ⚡ {energyUsed}</p>}
        <div className="mt-3 flex gap-2">
          {trained ? (
            <button data-testid="adv-end-session" disabled={working} onClick={onEndDay} className="h-12 w-full rounded-xl bg-secondary font-bold text-white disabled:opacity-60 active:scale-95">{working ? "…" : "End Session →"}</button>
          ) : (
            <>
              <button data-testid="adv-rest-btn" disabled={working || !careDone} onClick={() => doTrain(true)} className="h-12 flex-1 rounded-xl border-2 border-primary bg-card font-bold text-primary disabled:opacity-60 active:scale-95">Rest Day · ⚡100</button>
              <button data-testid="adv-train-btn" disabled={working || !careDone || selected.length === 0} onClick={() => doTrain(false)} className="h-12 flex-[2] rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-40 active:scale-95">{working ? "Training…" : "Train"}</button>
            </>
          )}
        </div>
        {trained && <p className="mt-2 text-center text-xs font-semibold text-secondary">Session done. Add care if needed, then End Session.</p>}
      </div>
      {onCompete && <button data-testid="adv-go-compete" onClick={onCompete} className="w-full rounded-xl border border-emerald-300 py-2.5 text-xs font-bold text-emerald-700 active:scale-95">Enter a Competition →</button>}
    </div>
  );
};

const RecStat = ({ label, value }) => (
  <div className="rounded-lg bg-muted/50 px-3 py-2 text-center"><p className="text-[10px] uppercase text-muted-foreground">{label}</p><p className="font-display text-lg font-bold text-primary">{value}</p></div>
);

export const CompetitionPanel = ({ a }) => {
  const [shows, setShows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lb, setLb] = useState(false);
  const car = a.career || {};
  const [now, setNow] = useState(Date.now());
  const load = () => gameApi.get(`/game/shows?discipline=${encodeURIComponent(a.discipline)}`).then((r) => setShows(r.data)).catch(() => setShows({}));
  useEffect(() => { load(); const r = setInterval(load, 20000); return () => clearInterval(r); }, [a.discipline]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const enter = async (kind) => {
    setBusy(true);
    try { const { data } = await gameApi.post(`/game/alumni/${a.id}/enter-show`, { kind }); toast.success(`Entered! Score ${data.score} · currently ${data.placing} of ${data.total}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const mins = (iso) => { let s = Math.floor((new Date(iso) - now) / 1000); if (s <= 0) return "starting…"; const h = Math.floor(s / 3600); s %= 3600; const m = Math.floor(s / 60); const ss = s % 60; return h ? `${h}h ${m}m` : `${m}:${String(ss).padStart(2, "0")}`; };
  const ShowCard = ({ kind, label }) => {
    const s = shows?.[kind];
    const cost = kind === "championship" ? shows?.champ_entry_cost : shows?.show_entry_cost;
    const mine = (s?.entries || []).find((e) => e.aid === a.id);
    return (
      <div className="rounded-2xl border border-border bg-card p-4" data-testid={`show-card-${kind}`}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-primary">{label}</p>
          <span className="text-[11px] text-muted-foreground">{s ? `ends in ${mins(s.ends_at)} · ${s.total} entered` : "—"}</span>
        </div>
        {mine ? <p className="mt-1 text-xs font-bold text-emerald-600" data-testid={`my-place-${kind}`}>Entered · currently {mine.place} of {s.total} (score {mine.score})</p>
          : <button data-testid={`enter-${kind}-btn`} disabled={busy || !s} onClick={() => enter(kind)} className="mt-2 h-10 w-full rounded-xl bg-emerald-600 text-sm font-bold text-white active:scale-95">Enter this {kind === "championship" ? "Championship" : "Show"}{cost != null ? ` · ${cost} 🪙` : ""}</button>}
        {(s?.entries || []).slice(0, 5).map((e) => (
          <div key={e.aid} className="mt-1 flex justify-between text-[11px] text-muted-foreground"><span>#{e.place} {e.name} · {e.owner}</span><span>{e.score}</span></div>
        ))}
      </div>
    );
  };
  const soonEnd = ["show", "championship"].some((k) => { const iso = shows?.[k]?.ends_at; if (!iso) return false; const s = Math.floor((new Date(iso) - now) / 1000); return s <= 60; });
  return (
    <div className="mx-4 mt-3 space-y-3" data-testid="competition-panel">
      {soonEnd && (
        <div data-testid="new-show-banner" className="animate-pulse rounded-xl bg-amber-500/15 px-3 py-2 text-center text-xs font-bold text-amber-700 ring-1 ring-amber-500/40">
          🏇 A new {a.discipline} show is starting — results settling now!
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-secondary">{a.discipline} · {car.level || "Novice"}</p>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <RecStat label="Starts" value={car.starts || 0} /><RecStat label="Wins" value={car.wins || 0} /><RecStat label="Top 3" value={car.top3 || 0} />
          <RecStat label="Earnings" value={`${car.earnings || 0}🪙`} /><RecStat label="Points" value={car.points || 0} /><RecStat label="Best Score" value={car.best_score ? `🏆 ${car.best_score}` : "—"} />
        </div>
      </div>
      {shows === null ? <p className="text-center text-xs text-muted-foreground">Loading shows…</p> : <>
        <ShowCard kind="show" label="30-Minute Show" />
        <ShowCard kind="championship" label="24-Hour Championship" />
      </>}
      <button data-testid="view-rankings-btn" onClick={() => setLb(!lb)} className="w-full rounded-xl border border-border py-2 text-xs font-bold text-primary">{lb ? "Hide" : "View"} {a.discipline} Rankings</button>
      {lb && <DisciplineLeaderboard discipline={a.discipline} highlightAid={a.id} />}
    </div>
  );
};

export const AwardsPanel = ({ a }) => {
  const car = a.career || {};
  const sc = a.scores || {};
  const history = (car.history || []).slice().reverse();
  const ribbons = (car.history || []).filter((h) => h.ribbon);
  const RIBBON_EMOJI = { "Blue Ribbon": "🔵", "Red Ribbon": "🔴", "Yellow Ribbon": "🟡" };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };
  const kindLabel = (k) => k === "championship" ? "Championship" : k === "practice" ? "Practice" : "Show";
  return (
    <div className="mx-4 mt-3 space-y-3" data-testid="awards-panel">
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="wins-makeover">
        <p className="text-xs font-bold uppercase tracking-wider text-secondary">Makeover Competition</p>
        {sc.placing ? (
          <div className="mt-2">
            <p className="font-display text-2xl font-bold text-primary">{ordinal(sc.placing)} <span className="text-base font-normal text-muted-foreground">of {sc.total_entries}</span></p>
            <p className="mt-0.5 text-xs text-muted-foreground">Overall {sc.overall}{typeof a.class_wins === "number" && a.class_wins > 0 ? ` · ${a.class_wins} class win${a.class_wins === 1 ? "" : "s"}` : ""}{a.fan_favorite ? " · ⭐ Fan Favorite" : ""}</p>
          </div>
        ) : <p className="mt-1 text-xs text-muted-foreground">No makeover result recorded for this horse yet.</p>}
      </div>
      {a.discipline && (
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="wins-discipline">
          <p className="text-xs font-bold uppercase tracking-wider text-secondary">Discipline Career · {a.discipline}</p>
          <div className="mt-2 grid grid-cols-4 gap-2">
            <WinStat label="Wins" value={car.wins || 0} />
            <WinStat label="Top 3" value={car.top3 || 0} />
            <WinStat label="Starts" value={car.starts || 0} />
            <WinStat label="Points" value={car.points || 0} />
            <WinStat label="Best Score" value={car.best_score ? `🏆 ${car.best_score}` : "—"} />
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-secondary">Titles</p>
        {(car.awards || []).length ? <div className="mt-2 flex flex-wrap gap-1.5">{car.awards.map((t) => <span key={t} className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">🏆 {t}</span>)}</div>
          : <p className="mt-1 text-xs text-muted-foreground">No titles yet — win shows to earn them.</p>}
      </div>
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="show-history">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wider text-secondary">Show History</p>
          <span className="text-[11px] text-muted-foreground">{car.starts || 0} starts · {car.wins || 0} wins · {ribbons.length} ribbons</span>
        </div>
        {history.length ? (
          <div className="mt-2 max-h-72 space-y-1.5 overflow-y-auto pr-1" data-testid="show-history-list">
            {history.map((h, i) => (
              <div key={i} data-testid={`show-history-row-${i}`} className="flex items-center gap-2 rounded-lg bg-muted/40 px-2.5 py-1.5">
                <span className="text-base leading-none">{h.ribbon ? RIBBON_EMOJI[h.ribbon] : "▫️"}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-primary">Placed {h.placing} of {h.total} <span className="font-normal text-muted-foreground">· {kindLabel(h.kind)}</span></p>
                  <p className="text-[10px] text-muted-foreground">{fmtDate(h.at)} · +{h.prize || 0}🪙 · +{h.points || 0} pts</p>
                </div>
                {h.placing === 1 && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">WIN</span>}
              </div>
            ))}
          </div>
        ) : <p className="mt-1 text-xs text-muted-foreground">No show results yet — enter a show to build this horse's record.</p>}
      </div>
    </div>
  );
};

const DISC_PERIODS = [{ k: "all", l: "All Time" }, { k: "month", l: "This Month" }, { k: "season", l: "This Season" }];

export const DisciplineLeaderboard = ({ discipline, highlightAid }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [reigning, setReigning] = useState({});
  const [period, setPeriod] = useState("all");
  const [sortBy, setSortBy] = useState("points");
  useEffect(() => {
    setRows(null);
    gameApi.get(`/game/disciplines/leaderboard?discipline=${encodeURIComponent(discipline)}&period=${period}&sort=${sortBy}`).then((r) => { setRows(r.data.entries); setReigning(r.data.reigning || {}); }).catch(() => { setRows([]); setReigning({}); });
  }, [discipline, period, sortBy]);
  const champ = period === "season" ? reigning.season : period === "month" ? reigning.month : null;
  return (
    <div className="rounded-2xl border border-border bg-card p-3" data-testid="discipline-leaderboard">
      <div className="mb-2 flex gap-1 rounded-xl bg-muted p-1">
        {DISC_PERIODS.map((pp) => (
          <button key={pp.k} data-testid={`disc-lb-period-${pp.k}`} onClick={() => setPeriod(pp.k)}
            className={`flex-1 rounded-lg py-1 text-[11px] font-bold ${period === pp.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{pp.l}</button>
        ))}
      </div>
      <div className="mb-2 flex gap-1 rounded-xl bg-muted p-1">
        {[["points", "Season Points"], ["score", "Top Score"]].map(([k, l]) => (
          <button key={k} data-testid={`disc-lb-sort-${k}`} onClick={() => setSortBy(k)}
            className={`flex-1 rounded-lg py-1 text-[11px] font-bold ${sortBy === k ? "bg-secondary text-white" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {champ && <p data-testid="reigning-champion" className="mb-2 flex items-center gap-1.5 rounded-xl bg-amber-100 px-2.5 py-1.5 text-[11px] font-bold text-amber-700"><Crown size={13} /> Reigning {period === "season" ? "Season" : "Month"} Champion: {champ.horse_name} · {champ.owner}</p>}
      {rows === null ? <p className="text-center text-xs text-muted-foreground">Loading…</p> : rows.length === 0 ? <p className="text-center text-xs text-muted-foreground">{sortBy === "score" ? "No horses have posted a score yet." : period === "all" ? "No ranked horses yet." : "No horses have scored this period yet."}</p> : rows.map((e) => (
        <button key={e.aid} type="button" data-testid={`lb-horse-${e.rank}`}
          onClick={() => e.owner_number && navigate(`/game/horse/${e.owner_number}/${e.aid}`)}
          className={`flex w-full items-center gap-2 border-b border-border/50 py-1.5 text-left last:border-0 hover:bg-muted/40 active:scale-[0.99] ${e.aid === highlightAid ? "bg-emerald-50" : ""}`}>
          <span className="w-6 text-center text-xs font-bold text-secondary">#{e.rank}</span>
          <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-primary">{champ && e.aid === champ.horse_id && <Crown size={12} className="mr-0.5 inline text-amber-500" />}{e.name}{e.deceased && <span title="Passed on" className="ml-1 text-[11px]">🕊️</span>} <span className="text-[10px] font-normal text-muted-foreground">· {e.owner}</span></p>
            <p className="text-[10px] text-muted-foreground">{e.level} · {e.wins}W · {e.starts} starts</p></div>
          <span className="text-sm font-bold text-emerald-600">{sortBy === "score" ? e.best_score : `${e.points} pts`}</span>
        </button>
      ))}
    </div>
  );
};

const WinStat = ({ label, value }) => (
  <div className="rounded-lg bg-muted/50 px-2 py-2 text-center"><p className="font-display text-lg font-bold text-primary">{value}</p><p className="text-[10px] uppercase text-muted-foreground">{label}</p></div>
);

export const LiveShowsBoard = ({ player }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [barnShows, setBarnShows] = useState({});
  const [now, setNow] = useState(Date.now());
  const [sel, setSel] = useState(null);
  const [pickAid, setPickAid] = useState("");
  const [entering, setEntering] = useState(false);
  const load = async () => {
    const pairs = await Promise.all(DISCIPLINES.map((d) =>
      gameApi.get(`/game/shows?discipline=${encodeURIComponent(d)}`).then((r) => [d, r.data]).catch(() => [d, null])));
    setData(Object.fromEntries(pairs));
    try {
      const r = await gameApi.get("/game/barns/shows/open");
      const g = {};
      (r.data.shows || []).forEach((s) => { (g[s.discipline] = g[s.discipline] || []).push(s); });
      setBarnShows(g);
    } catch { setBarnShows({}); }
  };
  useEffect(() => { load(); const r = setInterval(load, 20000); return () => clearInterval(r); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const fmt = (iso) => { if (!iso) return "—"; let s = Math.floor((new Date(iso) - now) / 1000); if (s <= 0) return "opening…"; const h = Math.floor(s / 3600); s %= 3600; const m = Math.floor(s / 60); const ss = s % 60; return h ? `${h}h ${m}m` : `${m}:${String(ss).padStart(2, "0")}`; };
  const key = (d) => d.replace(/\s+/g, "-").toLowerCase();
  const openSel = (v) => { setPickAid(""); setSel(v); };
  const eligibleFor = (s) => (player?.alumni || []).filter((a) => a.decision === "keep" && a.discipline === s.discipline && !(s.s.entries || []).some((e) => e.aid === a.id));
  const doEnter = async () => {
    if (!pickAid || !sel) return;
    setEntering(true);
    try {
      if (sel.kind === "barn") {
        const r = await gameApi.post(`/game/barns/shows/${sel.s.id}/enter`, { aid: pickAid });
        toast.success(`Entered ${sel.s.name}! Score ${r.data.score} · currently ${r.data.placing} of ${r.data.total}`);
        await load();
        const fresh = await gameApi.get(`/game/barns/shows/${sel.s.id}`);
        setSel({ ...sel, s: fresh.data });
      } else {
        const r = await gameApi.post(`/game/alumni/${pickAid}/enter-show`, { kind: sel.kind });
        toast.success(`Entered! Score ${r.data.score} · currently ${r.data.placing} of ${r.data.total}`);
        await load();
        const fresh = await gameApi.get(`/game/shows?discipline=${encodeURIComponent(sel.discipline)}`);
        const s2 = fresh.data[sel.kind];
        if (s2) setSel({ ...sel, s: s2 });
      }
      setPickAid("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not enter"); }
    finally { setEntering(false); }
  };
  const doEnterAll = async () => {
    if (!sel) return;
    setEntering(true);
    try {
      if (sel.kind === "barn") {
        const r = await gameApi.post(`/game/barns/shows/${sel.s.id}/enter-all`);
        toast.success(`Entered ${r.data.entered} horse(s)${r.data.skipped ? ` · ${r.data.skipped} skipped (coins/entered)` : ""}`);
        await load();
        const fresh = await gameApi.get(`/game/barns/shows/${sel.s.id}`);
        setSel({ ...sel, s: fresh.data });
      } else {
        const r = await gameApi.post("/game/disciplines/enter-all-open", { discipline: sel.discipline, kind: sel.kind });
        toast.success(`Entered ${r.data.entered} horse(s)${r.data.skipped ? ` · ${r.data.skipped} skipped (coins/entered)` : ""}`);
        await load();
        const fresh = await gameApi.get(`/game/shows?discipline=${encodeURIComponent(sel.discipline)}`);
        const s2 = fresh.data[sel.kind];
        if (s2) setSel({ ...sel, s: s2 });
      }
      setPickAid("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not enter"); }
    finally { setEntering(false); }
  };
  if (!data) return <p className="text-center text-xs text-muted-foreground" data-testid="live-shows-loading">Loading live shows…</p>;
  return (
    <div className="space-y-2" data-testid="live-shows-board">
      {DISCIPLINES.map((d) => {
        const row = data[d] || {};
        return (
          <div key={d} className="rounded-2xl border border-border bg-card p-3" data-testid={`live-disc-${key(d)}`}>
            <p className="text-sm font-bold text-primary">{d}</p>
            {row.show && (
              <button data-testid={`live-show-${key(d)}`} onClick={() => openSel({ discipline: d, kind: "show", s: row.show })}
                className="mt-1.5 flex w-full items-center justify-between rounded-xl bg-emerald-50 px-3 py-2 text-left active:scale-[0.99]">
                <span className="text-xs font-semibold text-primary">30-Min Show · {row.show.total} entered</span>
                <span className="text-[11px] font-bold text-emerald-600">{row.show_entry_cost != null ? `${row.show_entry_cost}🪙 · ` : ""}closes in {fmt(row.show.ends_at)}</span>
              </button>
            )}
            {row.show && <p className="mt-1 text-[10px] text-muted-foreground">Next {d} show opens the moment this one closes.</p>}
            {row.championship && (
              <button data-testid={`live-champ-${key(d)}`} onClick={() => openSel({ discipline: d, kind: "championship", s: row.championship })}
                className="mt-1.5 flex w-full items-center justify-between rounded-xl bg-amber-50 px-3 py-2 text-left active:scale-[0.99]">
                <span className="text-xs font-semibold text-amber-700">Championship · {row.championship.total} entered</span>
                <span className="text-[11px] font-bold text-amber-600">{row.champ_entry_cost != null ? `${row.champ_entry_cost}🪙 · ` : ""}ends in {fmt(row.championship.ends_at)}</span>
              </button>
            )}
            {(barnShows[d] || []).map((s) => (
              <button key={s.id} data-testid={`live-barn-show-${s.id}`} onClick={() => openSel({ discipline: d, kind: "barn", s })}
                className="mt-1.5 flex w-full items-center justify-between rounded-xl bg-sky-50 px-3 py-2 text-left active:scale-[0.99]">
                <span className="min-w-0 truncate text-xs font-semibold text-sky-800">🏟️ {s.name} · Hosted by [{s.barn_tag}] · {s.total} entered</span>
                <span className="shrink-0 text-[11px] font-bold text-sky-600">{s.entry_fee != null ? `${s.entry_fee}🪙 · ` : ""}ends in {fmt(s.ends_at)}</span>
              </button>
            ))}
          </div>
        );
      })}
      {sel && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4" data-testid="live-show-modal" onClick={() => setSel(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-display text-lg font-bold text-primary">{sel.kind === "barn" ? sel.s.name : `${sel.discipline} ${sel.kind === "championship" ? "Championship" : "Show"}`}</p>
              <button data-testid="live-show-close" onClick={() => setSel(null)} className="rounded-full p-1 text-muted-foreground"><X size={18} /></button>
            </div>
            <p className="text-[11px] text-muted-foreground">{sel.kind === "barn" ? `Hosted by ${sel.s.barn_name} [${sel.s.barn_tag}] · ` : ""}{sel.kind === "championship" ? "Ends" : "Closes"} in {fmt(sel.s.ends_at)} · {sel.s.total} entered</p>
            <div className="mt-2 max-h-72 space-y-1 overflow-y-auto pr-1">
              {(sel.s.entries || []).length ? sel.s.entries.map((e) => (
                <button key={e.aid} type="button" data-testid={`live-entry-${e.place}`}
                  onClick={() => e.owner_number && navigate(`/game/horse/${e.owner_number}/${e.aid}`)}
                  className="flex w-full items-center justify-between rounded-lg bg-muted/40 px-2.5 py-1.5 text-left text-xs hover:bg-muted active:scale-[0.99]">
                  <span className="min-w-0 flex-1 truncate font-semibold text-primary">#{e.place} {e.name} <span className="font-normal text-muted-foreground">· {e.owner}</span></span>
                  <span className="ml-2 shrink-0 font-bold text-emerald-600">{e.score}</span>
                </button>
              )) : <p className="py-4 text-center text-xs text-muted-foreground">No horses entered yet — be the first!</p>}
            </div>
            {(() => { const elig = eligibleFor(sel); return (
              <div className="mt-3 border-t border-border pt-3" data-testid="live-enter-row">
                <div className="flex items-center gap-2">
                  <select data-testid="live-enter-select" value={pickAid} onChange={(e) => setPickAid(e.target.value)}
                    className="min-w-0 flex-1 rounded-lg border border-border bg-background px-2 py-2 text-xs">
                    <option value="">{elig.length ? "Select your horse…" : "No eligible horse"}</option>
                    {elig.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  <button data-testid="live-enter-btn" disabled={!pickAid || entering} onClick={doEnter}
                    className="shrink-0 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground disabled:opacity-40">{entering ? "Entering…" : `Enter${(() => { if (sel.kind === "barn") return sel.s.entry_fee != null ? ` · ${sel.s.entry_fee}🪙` : ""; const c = (data?.[sel.discipline] || {})[sel.kind === "championship" ? "champ_entry_cost" : "show_entry_cost"]; return c != null ? ` · ${c}🪙` : ""; })()}`}</button>
                </div>
                {elig.length > 1 && (
                  <button data-testid="live-enter-all-btn" disabled={entering} onClick={doEnterAll}
                    className="mt-2 w-full rounded-lg border border-emerald-300 bg-emerald-50 py-2 text-xs font-bold text-emerald-700 disabled:opacity-40">
                    {entering ? "Entering…" : `Enter all ${elig.length} eligible`}
                  </button>
                )}
                {elig.length === 0 && <p className="mt-1 text-[10px] text-muted-foreground">Keep a {sel.discipline} horse (discipline chosen, not already entered) to compete here.</p>}
              </div>
            ); })()}
          </div>
        </div>
      )}
    </div>
  );
};

export { isEligible };
